import type { DataRow } from "../types";

export type AnalysisFilters = {
  workTypes: string[];
  dateFrom: string;
  dateTo: string;
  hourFrom: number;
  hourTo: number;
  dma: string;
  minCount: number;
  windowDays: number;
  radiusMeters: number;
  distinctDays: boolean;
  hotspotsOnly: boolean;
};

export type IncidentHotspot = {
  id: string;
  label: string;
  dmaCode: string | null;
  latitude: number | null;
  longitude: number | null;
  count: number;
  distinctDays: number;
  latestAt: string;
  workTypes: string[];
  rows: DataRow[];
};

export type FilterPlan = {
  filters: AnalysisFilters;
  summary: string[];
  warnings: string[];
};

export const defaultAnalysisFilters: AnalysisFilters = {
  workTypes: [], dateFrom: "", dateTo: "", hourFrom: 0, hourTo: 23, dma: "",
  minCount: 3, windowDays: 7, radiusMeters: 50, distinctDays: true, hotspotsOnly: true,
};

export function eventDateTime(row: DataRow): string | null {
  return row.receivedAt ?? row.openedAt ?? null;
}

export function eventHour(row: DataRow): number | null {
  const value = eventDateTime(row);
  if (!value?.includes("T")) return null;
  const hour = Number(value.slice(11, 13));
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null;
}

export function applyAnalysisFilters(rows: DataRow[], filters: AnalysisFilters): DataRow[] {
  return rows.filter((row) => {
    const dateTime = eventDateTime(row);
    const date = dateTime?.slice(0, 10) ?? "";
    const hour = eventHour(row);
    if (filters.workTypes.length && !filters.workTypes.includes(row.workType ?? "ไม่ระบุ")) return false;
    if (filters.dateFrom && (!date || date < filters.dateFrom)) return false;
    if (filters.dateTo && (!date || date > filters.dateTo)) return false;
    if (filters.dma && row.dmaCode !== filters.dma.replaceAll("-", "")) return false;
    if (filters.hourFrom !== 0 || filters.hourTo !== 23) {
      if (hour === null) return false;
      const inRange = filters.hourFrom <= filters.hourTo
        ? hour >= filters.hourFrom && hour <= filters.hourTo
        : hour >= filters.hourFrom || hour <= filters.hourTo;
      if (!inRange) return false;
    }
    return true;
  });
}

function normalizePlace(value: string | null | undefined): string {
  return String(value ?? "").trim().toLocaleLowerCase("th").replace(/\s+/g, " ");
}

function distanceMeters(a: DataRow, b: DataRow): number {
  if (a.latitude === null || a.longitude === null || b.latitude === null || b.longitude === null) return Number.POSITIVE_INFINITY;
  const latScale = 111_320;
  const lonScale = 111_320 * Math.cos(((a.latitude + b.latitude) / 2) * Math.PI / 180);
  return Math.hypot((a.latitude - b.latitude) * latScale, (a.longitude - b.longitude) * lonScale);
}

type Cluster = { id: string; rows: DataRow[]; latitude: number | null; longitude: number | null; dmaCode: string | null; label: string };

function spatialClusters(rows: DataRow[], radiusMeters: number): Cluster[] {
  const clusters: Cluster[] = [];
  const buckets = new Map<string, number[]>();
  const fallback = new Map<string, Cluster>();
  const latStep = radiusMeters / 111_320;
  const lonStep = radiusMeters / 107_000;
  for (const row of rows) {
    const dma = row.dmaCode ?? "NO-DMA";
    if (row.latitude === null || row.longitude === null) {
      const road = normalizePlace(row.road);
      const soi = normalizePlace(row.soi);
      const place = [road, soi].filter(Boolean).join(" / ") || normalizePlace(row.address);
      if (!place) continue;
      const key = `${dma}|place|${place}`;
      const existing = fallback.get(key);
      if (existing) existing.rows.push(row);
      else fallback.set(key, { id: key, rows: [row], latitude: null, longitude: null, dmaCode: row.dmaCode, label: [row.road, row.soi].filter(Boolean).join(" / ") || "ที่อยู่เดียวกัน" });
      continue;
    }
    const gx = Math.floor(row.latitude / latStep);
    const gy = Math.floor(row.longitude / lonStep);
    let bestIndex = -1;
    let bestDistance = radiusMeters + 1;
    for (let dx = -1; dx <= 1; dx += 1) for (let dy = -1; dy <= 1; dy += 1) {
      const candidates = buckets.get(`${dma}|${gx + dx}|${gy + dy}`) ?? [];
      for (const index of candidates) {
        const candidate = clusters[index];
        const representative = { ...row, latitude: candidate.latitude, longitude: candidate.longitude };
        const distance = distanceMeters(row, representative);
        if (distance <= radiusMeters && distance < bestDistance) { bestIndex = index; bestDistance = distance; }
      }
    }
    if (bestIndex >= 0) {
      const cluster = clusters[bestIndex];
      const nextCount = cluster.rows.length + 1;
      cluster.latitude = ((cluster.latitude ?? row.latitude) * cluster.rows.length + row.latitude) / nextCount;
      cluster.longitude = ((cluster.longitude ?? row.longitude) * cluster.rows.length + row.longitude) / nextCount;
      cluster.rows.push(row);
    } else {
      const index = clusters.length;
      clusters.push({ id: `${dma}|geo|${gx}|${gy}|${index}`, rows: [row], latitude: row.latitude, longitude: row.longitude, dmaCode: row.dmaCode, label: `รัศมี ${radiusMeters} ม.` });
      const key = `${dma}|${gx}|${gy}`;
      buckets.set(key, [...(buckets.get(key) ?? []), index]);
    }
  }
  return [...clusters, ...fallback.values()];
}

function bestWindow(rows: DataRow[], days: number): DataRow[] {
  const dated = rows.filter((row) => eventDateTime(row)).sort((a, b) => eventDateTime(a)!.localeCompare(eventDateTime(b)!));
  let left = 0; let best: DataRow[] = [];
  const span = Math.max(1, days) * 86_400_000;
  for (let right = 0; right < dated.length; right += 1) {
    const rightTime = Date.parse(eventDateTime(dated[right])!);
    while (left < right && rightTime - Date.parse(eventDateTime(dated[left])!) >= span) left += 1;
    if (right - left + 1 > best.length) best = dated.slice(left, right + 1);
  }
  return best;
}

export function buildIncidentHotspots(rows: DataRow[], filters: AnalysisFilters): IncidentHotspot[] {
  return spatialClusters(rows, filters.radiusMeters).map((cluster) => {
    const windowRows = bestWindow(cluster.rows, filters.windowDays);
    const dates = new Set(windowRows.map((row) => eventDateTime(row)?.slice(0, 10)).filter(Boolean));
    const roads = [...new Set(windowRows.map((row) => [row.road, row.soi].filter(Boolean).join(" / ")).filter(Boolean))];
    return {
      id: cluster.id,
      label: roads[0] ?? cluster.label,
      dmaCode: cluster.dmaCode,
      latitude: cluster.latitude,
      longitude: cluster.longitude,
      count: windowRows.length,
      distinctDays: dates.size,
      latestAt: windowRows.length ? eventDateTime(windowRows[windowRows.length - 1]) ?? "" : "",
      workTypes: [...new Set(windowRows.map((row) => row.workType ?? "ไม่ระบุ"))],
      rows: windowRows,
    };
  }).filter((item) => item.count >= filters.minCount && (!filters.distinctDays || item.distinctDays >= Math.min(filters.minCount, 2)))
    .sort((a, b) => b.count - a.count || b.latestAt.localeCompare(a.latestAt));
}

function latestDate(rows: DataRow[]): string {
  return rows.map((row) => eventDateTime(row)?.slice(0, 10) ?? "").sort().at(-1) ?? "";
}

function shiftDate(date: string, delta: number): string {
  const value = new Date(`${date}T00:00:00+07:00`);
  value.setDate(value.getDate() + delta);
  return value.toISOString().slice(0, 10);
}

export function planFiltersFromQuestion(question: string, rows: DataRow[], current: AnalysisFilters): FilterPlan {
  const next = { ...current, workTypes: [...current.workTypes] };
  const summary: string[] = [];
  const warnings: string[] = [];
  const normalized = question.trim().toLocaleLowerCase("th");
  const categories = [...new Set(rows.map((row) => row.workType).filter(Boolean))] as string[];
  const synonymPatterns: [RegExp, RegExp][] = [
    [/น้ำ(?:ไหล)?อ่อน|แรงดันต่ำ/, /น้ำ.*อ่อน|แรงดัน.*ต่ำ/],
    [/ท่อแตก|ท่อรั่ว/, /ท่อ.*(?:แตก|รั่ว)/],
    [/น้ำไม่ไหล/, /น้ำ.*ไม่.*ไหล/],
    [/ประตูน้ำ/, /ประตูน้ำ/],
  ];
  const matched = categories.filter((category) => {
    const compact = category.toLocaleLowerCase("th").replace(/\s+/g, "");
    if (normalized.includes(compact) || compact.includes(normalized.replace(/\s+/g, ""))) return true;
    return synonymPatterns.some(([queryPattern, categoryPattern]) => queryPattern.test(normalized) && categoryPattern.test(category));
  });
  if (matched.length) { next.workTypes = matched; summary.push(`ลักษณะงาน ${matched.join(", ")}`); }

  const minMatch = normalized.match(/(?:อย่างน้อย|ไม่น้อยกว่า)\s*(\d+)\s*(?:ครั้ง|งาน)/);
  if (minMatch) next.minCount = Math.max(2, Number(minMatch[1]));
  const windowMatch = normalized.match(/(?:ภายใน|ช่วง|ย้อนหลัง)\s*(\d+)\s*วัน/);
  if (windowMatch) next.windowDays = Math.max(1, Number(windowMatch[1]));
  if (/ซ้ำ|จุดเดิม|บริเวณเดิม|หลายวัน/.test(normalized)) next.hotspotsOnly = true;
  if (/หลายวัน|ต่างวัน/.test(normalized)) next.distinctDays = true;
  summary.push(`เหตุซ้ำอย่างน้อย ${next.minCount} ครั้ง ภายใน ${next.windowDays} วัน`);

  const recentMatch = normalized.match(/(?:ย้อนหลัง|ล่าสุด)\s*(\d+)\s*วัน/);
  const maxDate = latestDate(rows);
  if (recentMatch && maxDate) {
    next.dateTo = maxDate;
    next.dateFrom = shiftDate(maxDate, -(Number(recentMatch[1]) - 1));
    summary.push(`ข้อมูลวันที่ ${next.dateFrom} ถึง ${next.dateTo}`);
  }
  const timeMatch = normalized.match(/(\d{1,2})(?::\d{2})?\s*(?:-|ถึง)\s*(\d{1,2})(?::\d{2})?/);
  if (timeMatch) { next.hourFrom = Math.min(23, Number(timeMatch[1])); next.hourTo = Math.min(23, Number(timeMatch[2])); }
  else if (/เช้า/.test(normalized)) { next.hourFrom = 6; next.hourTo = 11; }
  else if (/บ่าย/.test(normalized)) { next.hourFrom = 12; next.hourTo = 15; }
  else if (/เย็น/.test(normalized)) { next.hourFrom = 16; next.hourTo = 19; }
  else if (/กลางคืน|ค่ำ/.test(normalized)) { next.hourFrom = 20; next.hourTo = 5; }
  if (next.hourFrom !== 0 || next.hourTo !== 23) summary.push(`ช่วงเวลา ${String(next.hourFrom).padStart(2, "0")}:00–${String(next.hourTo).padStart(2, "0")}:59`);

  const dmaMatch = normalized.match(/(?:dma|ดีเอ็มเอ)\s*([0-9-]{6,8})/i);
  if (dmaMatch) { next.dma = dmaMatch[1].replaceAll("-", ""); summary.push(`DMA ${next.dma}`); }
  if (!matched.length && /(น้ำ|ท่อ|มาตร|ประตู|แรงดัน)/.test(normalized)) warnings.push("ยังจับคู่คำถามกับลักษณะงานในชุดข้อมูลไม่ได้ กรุณาเลือกจากตัวกรองก่อนยืนยัน");
  if (!rows.some((row) => eventHour(row) !== null)) warnings.push("ชุดข้อมูล active นี้ยังไม่มีเวลารับแจ้งที่ normalize แล้ว ต้องนำเข้าไฟล์เดิมอีกครั้งเพื่อวิเคราะห์รายชั่วโมง");
  return { filters: next, summary, warnings };
}
